import React from "react";
import Container from "../../components/Container/Container";
import BulkUserManagementSection from "./components/BulkUserManagementSection";
import ManualCertificateSection from "./components/ManualCertificateSection";
import UserProductManagementSection from "./components/UserProductManagementSection";
import { useAdminProducts } from "./hooks/useAdminProducts";
import { useBulkUserManagement } from "./hooks/useBulkUserManagement";
import { useManualCertificateManagement } from "./hooks/useManualCertificateManagement";
import { useUserProductEditor } from "./hooks/useUserProductEditor";
import "./admin.scss";

const Admin: React.FC = () => {
  const { availableProducts, adminLimits, productsLoading, adminLoadError } = useAdminProducts();
  const bulkManagement = useBulkUserManagement();
  const userProductEditor = useUserProductEditor();
  const manualCertificate = useManualCertificateManagement({ availableProducts });

  return (
    <Container type="wide">
      <div className="admin-page">
        <h2 className="admin-page__heading">Administratoriaus puslapis</h2>

        <div className="admin-page__cards">
          <BulkUserManagementSection
            bulkEmailsInput={bulkManagement.bulkEmailsInput}
            onBulkEmailsChange={bulkManagement.setBulkEmailsInput}
            normalizedBulkEmails={bulkManagement.normalizedBulkEmails}
            availableProducts={availableProducts}
            adminLimits={adminLimits}
            productsLoading={productsLoading}
            adminLoadError={adminLoadError}
            selectedBulkProducts={bulkManagement.selectedBulkProducts}
            isBulkProductDropdownOpen={bulkManagement.isBulkProductDropdownOpen}
            onBulkProductDropdownOpenChange={bulkManagement.setBulkProductDropdownOpen}
            onToggleBulkProduct={bulkManagement.toggleBulkProductSelection}
            bulkError={bulkManagement.bulkError}
            bulkCheckLoading={bulkManagement.bulkCheckLoading}
            bulkCreateLoading={bulkManagement.bulkCreateLoading}
            onBulkCheck={bulkManagement.handleBulkCheck}
            onBulkCreate={bulkManagement.handleBulkCreate}
            bulkCreateResponse={bulkManagement.bulkCreateResponse}
            resultRows={bulkManagement.resultRows}
          />

          <UserProductManagementSection
            lookupEmail={userProductEditor.lookupEmail}
            onLookupEmailChange={userProductEditor.setLookupEmail}
            lookupLoading={userProductEditor.lookupLoading}
            lookupError={userProductEditor.lookupError}
            updateMessage={userProductEditor.updateMessage}
            lookupUser={userProductEditor.lookupUser}
            editableProducts={userProductEditor.editableProducts}
            updateLoading={userProductEditor.updateLoading}
            availableProducts={availableProducts}
            isLookupProductDropdownOpen={userProductEditor.isLookupProductDropdownOpen}
            onLookupProductDropdownOpenChange={userProductEditor.setLookupProductDropdownOpen}
            onToggleEditableProduct={userProductEditor.toggleEditableProductSelection}
            onLookupUser={userProductEditor.handleLookupUser}
            onUpdateProducts={userProductEditor.handleUpdateProducts}
          />
        </div>

        <ManualCertificateSection
          certificateEnabledProducts={manualCertificate.certificateEnabledProducts}
          manualSelectedProduct={manualCertificate.manualSelectedProduct}
          manualName={manualCertificate.manualName}
          manualSurname={manualCertificate.manualSurname}
          manualBirthDate={manualCertificate.manualBirthDate}
          manualEmail={manualCertificate.manualEmail}
          manualOptionalEmail={manualCertificate.manualOptionalEmail}
          manualCompany={manualCertificate.manualCompany}
          manualFieldCode={manualCertificate.manualFieldCode}
          manualProductCode={manualCertificate.manualProductCode}
          manualHours={manualCertificate.manualHours}
          manualDate={manualCertificate.manualDate}
          manualCountNumber={manualCertificate.manualCountNumber}
          manualPdfBlob={manualCertificate.manualPdfBlob}
          manualActionLoading={manualCertificate.manualActionLoading}
          manualMessage={manualCertificate.manualMessage}
          manualError={manualCertificate.manualError}
          manualLastSavedCountNumber={manualCertificate.manualLastSavedCountNumber}
          manualCertificationCheckLoading={manualCertificate.manualCertificationCheckLoading}
          manualCertificationStatus={manualCertificate.manualCertificationStatus}
          manualCertificationMessage={manualCertificate.manualCertificationMessage}
          manualSelectedProductTitle={manualCertificate.manualSelectedProductTitle}
          isManualCertificationCheckCurrent={manualCertificate.isManualCertificationCheckCurrent}
          isManualConfirmationModalOpen={manualCertificate.isManualConfirmationModalOpen}
          requiresManualFieldCode={manualCertificate.requiresManualFieldCode}
          fieldCodeOptions={manualCertificate.HB_FIELD_CODE_OPTIONS}
          productsLoading={productsLoading}
          onManualProductChange={manualCertificate.handleManualProductChange}
          onManualNameChange={manualCertificate.setManualName}
          onManualSurnameChange={manualCertificate.setManualSurname}
          onManualBirthDateChange={manualCertificate.setManualBirthDate}
          onManualEmailChange={manualCertificate.setManualEmail}
          onManualOptionalEmailChange={manualCertificate.setManualOptionalEmail}
          onManualCompanyChange={manualCertificate.setManualCompany}
          onManualFieldCodeChange={manualCertificate.setManualFieldCode}
          onManualDateChange={manualCertificate.setManualDate}
          onManualCertificationCheck={manualCertificate.handleManualCertificationCheck}
          onManualCompletion={manualCertificate.handleManualCompletion}
          onCloseManualConfirmationModal={manualCertificate.closeManualConfirmationModal}
          onConfirmManualCompletion={manualCertificate.confirmManualCompletion}
          onDownloadManualPreview={manualCertificate.downloadManualPreview}
        />
      </div>
    </Container>
  );
};

export default Admin;
